import { Stack } from '@mui/material';
import { itemStatus } from '@nohara-dxdev/mt-common';
import { useEffect, useMemo, useRef } from 'react';
import {
  TextFieldElement,
  SelectElement,
  useFormContext,
  AutocompleteElement,
} from 'react-hook-form-mui';
import { useTranslation } from 'react-i18next';

import { useBuildings } from '@/features/buildings/api/get-buildings';
import { LocationAssignmentSelectInput } from '@/features/buildings/components/form/location-assignment-select-input';
import { RoomFilterProvider } from '@/features/buildings/providers/room-filter-provider';
import { useSettingValues } from '@/features/settings';
import { SystemSelectInput } from '@/features/systems/components/form/system-select-input';
import { ItemTemplateEntity, SettingIdValue } from '@/types/api';
import { Location, LocationType } from '@/types/location';

import { getStatusLabel } from '../../utils/get-item-status-label';

import { ItemCircuitForm } from './item-circuit-form';

export interface ItemInfoFormProps {
  itemTemplate: Pick<ItemTemplateEntity, 'isBoardItem' | 'constructionTypeIds' | 'itemTypeId'>;
  mode?: 'editMode' | 'previewMode';
}

export const ItemInfoForm = ({ itemTemplate, mode = 'editMode' }: ItemInfoFormProps) => {
  const { t } = useTranslation();
  const { getSettingValue } = useSettingValues();
  const form = useFormContext();
  const { data: buildings = [] } = useBuildings();

  const quotationTypes = getSettingValue<SettingIdValue<string>[]>('quotationTypes', []);
  const locationTypes = getSettingValue<SettingIdValue<string>[]>('locationTypes', []);
  const itemTypes = getSettingValue<SettingIdValue<string>[]>('itemTypes', []);

  const quotationTypeOptions = useMemo(
    () => quotationTypes.map(({ id, value }) => ({ id, label: value })),
    [quotationTypes],
  );

  const locationTypeOptions = useMemo(() => {
    return [
      { id: '', label: t('itemTemplates.detail.selectPlaceholder') },
      ...locationTypes.map(({ id, value }) => ({ id, label: value })),
    ];
  }, [locationTypes, t]);

  const statusOptions = useMemo(
    () =>
      Object.values(itemStatus).map((status) => ({
        id: status,
        label: getStatusLabel(status, t),
      })),
    [t],
  );

  // Check if item type is 建具 (furniture)
  const isFurnitureType = useMemo(() => {
    const furnitureType = itemTypes.find((type) => type.value === '建具');
    return furnitureType?.id === itemTemplate.itemTypeId;
  }, [itemTypes, itemTemplate.itemTypeId]);

  const location = form.watch('location') as Location | null | undefined;
  const isSubmitting = form.formState.isSubmitting;
  const isInitialMount = useRef(true);
  const isClearingAdjacentLocation = useRef(false);

  // Find the floorId of the selected location (if it's a room)
  const selectedLocationFloorId = useMemo(() => {
    if (!location || location.type !== LocationType.ROOM) {
      return undefined;
    }
    for (const building of buildings) {
      for (const floor of building.floors) {
        const room = floor.rooms.find((r) => r.id === location.id);
        if (room) return floor.id;
      }
    }
    return undefined;
  }, [location, buildings]);

  // Clear adjacentLocation when location changes or item type is not furniture
  // Also clear if adjacentLocation no longer satisfies constraints
  // Skip on initial mount to preserve initialData
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Prevent infinite loop if we're already clearing
    if (isClearingAdjacentLocation.current) {
      return;
    }

    // Get current adjacentLocation value
    const currentAdjacentLocation = form.getValues('adjacentLocation') as
      | Location
      | null
      | undefined;

    // Clear adjacentLocation if item type is not furniture
    if (!isFurnitureType) {
      if (currentAdjacentLocation) {
        isClearingAdjacentLocation.current = true;
        form.setValue('adjacentLocation', null, { shouldDirty: false });
        // Reset flag after a microtask to allow the form state to update
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        Promise.resolve().then(() => {
          isClearingAdjacentLocation.current = false;
        });
      }
      return;
    }

    // If no adjacentLocation, nothing to validate
    if (!currentAdjacentLocation) {
      return;
    }

    // If location is null, no constraint can be violated — leave adjacentLocation untouched
    if (!location) {
      return;
    }

    // If location is a room, check if adjacentLocation still satisfies constraints
    if (location.type === LocationType.ROOM) {
      // If adjacentLocation is a space (outdoor or otherSpaces), it's always allowed
      if (
        currentAdjacentLocation.type === LocationType.OUTDOOR ||
        currentAdjacentLocation.type === LocationType.OTHER_SPACES
      ) {
        // Spaces are always allowed, no need to clear
        return;
      }

      // If adjacentLocation is a room, it must be on the same floor as location
      if (currentAdjacentLocation.type === LocationType.ROOM) {
        const locationFloorId = selectedLocationFloorId;

        // Find the floorId of the adjacent location
        let adjLocationFloorId: string | undefined;
        for (const building of buildings) {
          for (const floor of building.floors) {
            const room = floor.rooms.find((r) => r.id === currentAdjacentLocation.id);
            if (room) {
              adjLocationFloorId = floor.id;
              break;
            }
          }
          if (adjLocationFloorId) break;
        }

        // If floors don't match, clear adjacentLocation
        if (locationFloorId && adjLocationFloorId && locationFloorId !== adjLocationFloorId) {
          isClearingAdjacentLocation.current = true;
          form.setValue('adjacentLocation', null, { shouldDirty: false });
          // Reset flag after a microtask to allow the form state to update
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          Promise.resolve().then(() => {
            isClearingAdjacentLocation.current = false;
          });
        }
      }
    }

    // If location is a space, adjacentLocation can be anything (no restriction)
    // No need to clear in this case
  }, [isFurnitureType, location, selectedLocationFloorId, buildings, form]);

  // Determine if adjacent location field should be enabled
  const isAdjacentLocationEnabled = isFurnitureType && !isSubmitting;

  // Get exclude location IDs (exclude current location)
  const excludeLocationIds = useMemo(() => {
    return location ? [location.id] : [];
  }, [location]);

  // Determine if we should apply floor filter for adjacent location
  // Only apply floor filter if location is a room (not a space)
  const shouldApplyFloorFilter = location?.type === LocationType.ROOM;

  return (
    <Stack spacing={3}>
      {/* Item Name and Number */}
      <Stack direction="row">
        <TextFieldElement
          disabled={form.formState.disabled}
          label={t('itemTemplates.detail.itemName')}
          name="name"
          fullWidth
          required
        />
      </Stack>

      {/* Location and Category */}
      <Stack direction="row" spacing={2}>
        <TextFieldElement
          label={t('itemTemplates.detail.itemNumber')}
          name="itemNumber"
          fullWidth
          disabled={form.formState.disabled}
        />
        <SelectElement
          disabled={form.formState.disabled}
          name="locationTypeId"
          label={t('itemTemplates.detail.itemLocation')}
          fullWidth
          options={locationTypeOptions}
        />
      </Stack>

      <SelectElement
        disabled={form.formState.disabled}
        name="status"
        label={t('items.list.columns.decisionStatus')}
        fullWidth
        options={statusOptions}
      />

      <LocationAssignmentSelectInput name="location" disabled={isSubmitting} />

      {/* Adjacent Location - Only shown for furniture items (建具) */}
      {isFurnitureType && (
        <RoomFilterProvider
          filterValues={{
            floorIdFilter: shouldApplyFloorFilter ? selectedLocationFloorId : undefined,
            excludeRoomIds: excludeLocationIds.filter((id) => {
              // Only exclude room IDs, not space IDs
              // We need to check if the excluded ID is a room
              if (!location) return false;
              for (const building of buildings) {
                for (const floor of building.floors) {
                  if (floor.rooms.some((r) => r.id === id)) {
                    return true;
                  }
                }
              }
              return false;
            }),
          }}
        >
          <LocationAssignmentSelectInput
            name="adjacentLocation"
            placeholder={t('itemTemplates.detail.adjacentRoom', 'Select Adjacent Location')}
            disabled={!isAdjacentLocationEnabled || isSubmitting}
            excludeLocationIds={excludeLocationIds}
            rules={{
              validate: (value: Location | null) => {
                if (!isAdjacentLocationEnabled && value) {
                  return t('itemTemplates.detail.adjacentRoomRequiresRoom');
                }
                if (value && location && value.id === location.id) {
                  return t('itemTemplates.detail.adjacentRoomSameAsRoom');
                }
                return true;
              },
            }}
          />
        </RoomFilterProvider>
      )}

      <AutocompleteElement
        name="quotationTypeId"
        label={t('itemTemplates.detail.estimationCategory')}
        matchId
        options={quotationTypeOptions}
        textFieldProps={{ placeholder: t('itemTemplates.detail.selectPlaceholder') }}
        autocompleteProps={{
          fullWidth: true,
          disabled: form.formState.disabled,
          noOptionsText: t('itemTemplates.detail.noOptions'),
        }}
      />

      <SystemSelectInput constructionTypeIdOptions={itemTemplate.constructionTypeIds} />

      {/* Circuit */}
      {itemTemplate.isBoardItem && mode === 'editMode' && <ItemCircuitForm />}
    </Stack>
  );
};
